import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseReady } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';

interface BlogPostRow {
  id: string;
  slug: string;
  title: string;
  section?: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
}

export default function BlogListPage() {
  const [rows, setRows] = useState<BlogPostRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const onDelete = async (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setError(null);
    setDeletingId(id);
    try {
      const { error } = await supabase.from('blog_posts').delete().eq('id', id);
      if (error) throw error;
      setRows((rows) => (rows ? rows.filter((r) => r.id !== id) : rows));
    } catch (e: any) {
      setError(e.message || 'Failed to delete post');
    } finally {
      setDeletingId(null);
    }
  };


  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        if (!isSupabaseReady) {
          setRows([]);
          setError('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
          return;
        }
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, slug, title, section, status, published_at')
          .order('published_at', { ascending: false, nullsFirst: true });
        if (error) throw error;
        if (!ignore) setRows(data as BlogPostRow[]);
      } catch (e: any) {
        if (!ignore) setError(e.message || 'Failed to load posts');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, []);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Blog</h1>
          <p className="text-sm text-gray-600">Create and manage posts. Posts are global and appear across all tenants.</p>
        </div>
        <Button onClick={() => navigate('/system/blog/new')}>New Post</Button>
      </div>

      {loading && <div className="text-gray-600">Loading…</div>}
      {error && (
        <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border bg-white">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(rows || []).map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{r.title}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{r.slug}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{r.section || '—'}</td>
                <td className="px-4 py-3 text-sm">
                  {r.status === 'published' ? (
                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">Published</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Draft</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {r.published_at ? new Date(r.published_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <div className="flex justify-end gap-2">
                    <Link to={`/system/blog/${r.id}`} className="text-blue-700 hover:underline">Edit</Link>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={deletingId === r.id}
                      onClick={() => onDelete(r.id, r.title)}
                    >
                      {deletingId === r.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-600">No posts yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

